import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Upload, Loader2, CheckCircle2, ArrowUpRight, ArrowRight, Mail, Phone,
  MessageCircle, Plus, Minus, Zap,
} from "lucide-react";
import { SECTORS, relatedFor, SITE_ORIGIN } from "../data/sectors";
import { Reveal } from "../components/Reveal";
import { CONTACT } from "../data/site";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { trackLeadConversion, trackEvent } from "../lib/gtag";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

const PROCESS = [
  ["01", "Requirement", "Buyer sends drawing/specification."],
  ["02", "Requirement Review", "Technical and commercial requirements are understood."],
  ["03", "Supplier Identification", "Suitable Indian manufacturing sources are explored."],
  ["04", "RFQ", "Quotations are requested."],
  ["05", "Supplier Evaluation", "Capability and commercial suitability are reviewed."],
  ["06", "Sample", "Sample development where required."],
  ["07", "Quality", "Inspection and quality support."],
  ["08", "Production", "Production monitoring."],
  ["09", "Export", "Export documentation and shipment coordination."],
];

const TRUST = [
  ["Engineering-Focused Sourcing", "We understand manufacturing requirements and technical documentation."],
  ["Indian Supplier Network", "Access suitable Indian manufacturing sources based on the buyer's requirement."],
  ["RFQ Management", "Coordinate quotations and supplier communication."],
  ["Quality Support", "Support inspection and quality requirements."],
  ["Production Follow-Up", "Monitor progress where required."],
  ["Global Buyer Support", "Help international buyers coordinate sourcing from India."],
];

const track = (event, params = {}) => trackEvent(event, params);

function RfqForm({ sector }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [fileName, setFileName] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const f = e.target;
      const fd = new FormData();
      fd.set("name", f.name_.value);
      fd.set("company", f.company.value);
      fd.set("country", f.country.value);
      fd.set("email", f.email.value);
      fd.set("phone", f.phone.value);
      fd.set("commodity", `${sector.h1.split(" from ")[0]} — ${f.category.value || sector.componentsTitle}`);
      fd.set("quantity", f.quantity.value);
      const msgParts = [];
      if (f.category.value) msgParts.push(`Component Category: ${f.category.value}`);
      if (f.material.value) msgParts.push(`Material: ${f.material.value}`);
      if (f.delivery.value) msgParts.push(`Target Delivery: ${f.delivery.value}`);
      if (f.message.value) msgParts.push(`\nMessage:\n${f.message.value}`);
      msgParts.push(`\n[Landing Page: ${sector.slug}]`);
      fd.set("message", msgParts.join("\n"));
      if (f.drawing.files[0]) fd.set("drawing", f.drawing.files[0]);
      await api.post("/quote", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadConversion(`sector-${sector.slug}`);
      track("rfq_submit", { sector: sector.slug });
      setDone(true);
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      f.reset(); setFileName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      data-testid="rfq-success" className="border border-border bg-card p-10 text-center">
      <CheckCircle2 size={44} className="text-brand-orange mx-auto mb-5" />
      <h3 className="font-display font-black text-2xl mb-2">RFQ Received!</h3>
      <p className="text-muted-foreground mb-6">Our team is reviewing your requirement.</p>
      <button onClick={() => setDone(false)} className="border border-foreground px-6 py-3 font-mono uppercase text-[11px] tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">Submit Another</button>
    </motion.div>
  );

  return (
    <form onSubmit={onSubmit} data-testid="rfq-form" className="space-y-5 border border-border bg-card p-6 md:p-8">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className={labelCls}>Full Name *</label><input required name="name_" className={inputCls} placeholder="Full name" /></div>
        <div><label className={labelCls}>Company Name *</label><input required name="company" className={inputCls} placeholder="Your company" /></div>
        <div><label className={labelCls}>Business Email *</label><input required type="email" name="email" className={inputCls} placeholder="you@company.com" data-testid="rfq-email" /></div>
        <div><label className={labelCls}>Country *</label><input required name="country" className={inputCls} placeholder="e.g. Germany" /></div>
        <div><label className={labelCls}>Phone / WhatsApp</label><input name="phone" className={inputCls} placeholder="+1 ..." /></div>
        <div><label className={labelCls}>Component Category</label><input name="category" className={inputCls} placeholder="e.g. Shafts, Brackets, Housings" /></div>
        <div><label className={labelCls}>Material</label><input name="material" className={inputCls} placeholder="e.g. SS 304, AL 6061" /></div>
        <div><label className={labelCls}>Annual / Monthly Quantity *</label><input required name="quantity" className={inputCls} placeholder="e.g. 10,000 / year" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Target Delivery</label><input name="delivery" className={inputCls} placeholder="e.g. 6 weeks / Q1 2026" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Message / Requirements</label><textarea rows={3} name="message" className={inputCls} placeholder="Tolerances, certifications, target price, notes..." /></div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Upload Drawing / PDF / CAD</label>
          <label className="flex items-center gap-3 border border-dashed border-border px-4 py-3 cursor-pointer hover:border-brand-orange transition-colors">
            <Upload size={17} className="text-brand-orange" />
            <span className="text-muted-foreground text-sm truncate">{fileName || "Choose a file"}</span>
            <input type="file" name="drawing" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp"
              className="hidden" data-testid="rfq-file"
              onChange={(e) => { setFileName(e.target.files?.[0]?.name || ""); track("file_upload", { sector: sector.slug }); }} />
          </label>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="rfq-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[12px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting</> : "SUBMIT RFQ"}
      </button>
      <p className="text-center text-sm text-muted-foreground">
        Or email your requirement directly:{" "}
        <a href={`mailto:${CONTACT.email}`} onClick={() => track("email_click", { sector: sector.slug })} className="text-brand-orange">{CONTACT.email}</a>
      </p>
    </form>
  );
}

function FaqAccordion({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="border border-border divide-y divide-border">
      {faqs.map((f, i) => (
        <div key={f.q} className="bg-card">
          <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
            className="w-full flex items-center justify-between gap-4 text-left p-5 hover:text-brand-orange transition-colors">
            <span className="font-display font-bold">{f.q}</span>
            {open === i ? <Minus size={18} className="text-brand-orange shrink-0" /> : <Plus size={18} className="shrink-0" />}
          </button>
          {open === i && <div className="px-5 pb-5 -mt-1 text-muted-foreground leading-relaxed">{f.a}</div>}
        </div>
      ))}
    </div>
  );
}

export default function SectorLanding({ sectorKey }) {
  const sector = SECTORS[sectorKey];
  const wa = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent(`Hi SourceX Global, I have an RFQ for ${sector?.componentsTitle || ""}.`)}`;
  const related = sector ? relatedFor(sectorKey) : [];

  useSeo({
    title: sector?.seoTitle,
    description: sector?.metaDesc,
    keywords: sector?.keywords,
    canonical: sector ? `${SITE_ORIGIN}/${sector.slug}` : undefined,
  });

  if (!sector) return <Navigate to="/" replace />;

  return (
    <div data-testid={`sector-${sectorKey}`} className="pb-24 md:pb-0">
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-brand-orange/10 blur-3xl" />
        <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8">
          <p className="overline mb-5">Sourcing from India · For Global B2B Buyers</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1]">{sector.h1}</h1>
          <p className="mt-6 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed">{sector.heroText}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#rfq" data-testid="hero-rfq-btn" onClick={() => track("rfq_cta_click", { sector: sector.slug, placement: "hero" })}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors">
              REQUEST AN RFQ <ArrowUpRight size={16} />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              TALK TO SOURCEX GLOBAL
            </Link>
            <a href={wa} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { sector: sector.slug })}
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-4 font-mono uppercase text-[11px] tracking-[0.18em]">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={`mailto:${CONTACT.email}`} onClick={() => track("email_click", { sector: sector.slug })}
              className="inline-flex items-center gap-2 glass-dark px-5 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              <Mail size={15} /> Email
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => track("phone_click", { sector: sector.slug })}
              className="inline-flex items-center gap-2 glass-dark px-5 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              <Phone size={15} /> Call
            </a>
          </div>
        </div>
      </section>

      {/* COMPONENTS */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-16 md:py-24">
        <Reveal><p className="overline mb-3">Components</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-10">{sector.componentsTitle}</h2></Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border">
          {sector.components.map((c) => (
            <Reveal key={c} className="bg-card p-5">
              <div className="h-8 w-8 bg-brand-orange grid place-items-center mb-3"><Zap size={14} className="text-white" strokeWidth={2} fill="white" /></div>
              <div className="font-display font-bold text-sm">{c}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CAPABILITIES + MATERIALS */}
      <section className="bg-secondary py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <p className="overline mb-3">Capabilities</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-6">{sector.capabilitiesTitle}</h2>
            <ul className="space-y-2">
              {sector.capabilities.map((c) => (
                <li key={c} className="flex items-center gap-3 border-b border-border pb-2 text-foreground">
                  <span className="h-2 w-2 bg-brand-orange" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="overline mb-3">Materials</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-6">Materials Supported</h2>
            <div className="flex flex-wrap gap-2">
              {sector.materials.map((m) => (
                <span key={m} className="px-3.5 py-2 border border-border bg-card font-mono uppercase text-[11px] tracking-[0.1em]">{m}</span>
              ))}
            </div>
            <p className="overline mt-8 mb-3">Applications</p>
            <div className="grid grid-cols-2 gap-2">
              {sector.applications.map((a) => (
                <div key={a} className="border border-border bg-card px-3.5 py-2.5 text-sm">{a}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="overline mb-3">Sourcing Process</p>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-10">From RFQ to Export.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {PROCESS.map(([n, t, d]) => (
            <div key={n} className="bg-card p-6">
              <div className="font-mono text-brand-orange text-sm mb-2">{n}</div>
              <div className="font-display font-bold mb-1">{t}</div>
              <div className="text-muted-foreground text-sm">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-navy text-white py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <p className="overline mb-3">Why SourceX Global?</p>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-10">An engineering-focused sourcing partner.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {TRUST.map(([t, d]) => (
              <div key={t} className="bg-navy p-6">
                <div className="font-display font-bold text-lg mb-2">{t}</div>
                <div className="text-white/60 text-sm">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RFQ FORM */}
      <section id="rfq" className="bg-secondary py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <p className="overline mb-3">Have an Active RFQ?</p>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-3">Send us your drawing and requirement.</h2>
          <p className="text-muted-foreground mb-10">Our sourcing team can review your requirement and explore suitable Indian manufacturing sources.</p>
          <RfqForm sector={sector} />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="overline mb-3">FAQ</p>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-10">Sourcing questions, answered.</h2>
        <FaqAccordion faqs={sector.faqs} />
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-12 border-t border-border">
          <p className="overline mb-4">Related Sourcing</p>
          <div className="flex flex-wrap gap-3">
            {related.map((r) => (
              <Link key={r.to} to={r.to} className="inline-flex items-center gap-2 border border-border px-4 py-2.5 font-mono uppercase text-[11px] tracking-[0.15em] hover:border-brand-orange hover:text-brand-orange transition-colors">
                {r.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-brand-orange text-white py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-black text-3xl md:text-6xl tracking-tighter mb-6">{sector.cta}</h2>
          <p className="text-white/90 max-w-xl mx-auto mb-8">Send your drawing and requirement. Our sourcing team will respond shortly.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#rfq" className="bg-navy text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-navy-soft transition-colors">REQUEST AN RFQ</a>
            <Link to="/contact" className="border border-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white hover:text-brand-orange transition-colors">TALK TO SOURCEX GLOBAL</Link>
          </div>
        </div>
      </section>

      {/* STICKY MOBILE RFQ */}
      <a href="#rfq" data-testid="sticky-rfq"
        onClick={() => track("rfq_cta_click", { sector: sector.slug, placement: "sticky" })}
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-brand-orange text-white text-center py-4 font-mono uppercase text-[12px] tracking-[0.18em] shadow-2xl">
        ⚡ REQUEST AN RFQ
      </a>
    </div>
  );
}
